/**
 * The single place the UI asks "may this person see that".
 *
 * Every caller goes through these hooks rather than reading `user` directly,
 * which is what makes adding an authorization model an edit to this file
 * instead of a search across the codebase. Today the answers come from
 * `is_staff` and from being signed in at all; when the backend grows roles
 * (kdoshsop-api/docs/adr/0001) they come from `permissions`, `role_codes` and
 * `scope` on `/auth/me/`, and no call site changes.
 *
 * None of this is a security boundary. It decides what to render; the API
 * decides what is allowed.
 */
import { canSeeView, VIEWS, type ViewKey } from '@/config/views';
import { useAuth } from '@/features/auth/hooks/useAuth';

/** Can the signed-in account reach this screen? */
export function useCanSee(view?: ViewKey): boolean {
  const { user } = useAuth();
  if (!view) return true;
  return canSeeView(user, VIEWS[view]);
}

/**
 * Does the account hold a permission? An array means "any of these". Reads the
 * same `permissions` list `/auth/me/` publishes and `canSeeView` checks, so a
 * feature can adapt (which filters to show, which actions to render) without
 * duplicating the codename vocabulary.
 */
export function useHasPermission(permission: string | readonly string[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.is_superuser) return true;
  const needed = typeof permission === 'string' ? [permission] : permission;
  return needed.some((codename) => user.permissions.includes(codename));
}

/** Does the account have Django admin access? */
export function useIsStaff(): boolean {
  const { user } = useAuth();
  return user?.is_staff ?? false;
}

/**
 * The emergency account. Kept separate from `useIsStaff` because they answer
 * different questions, and conflating them is how "may administer the data"
 * and "bypasses every check" become one flag nobody can hand out safely.
 */
export function useIsSuperuser(): boolean {
  const { user } = useAuth();
  return user?.is_superuser ?? false;
}
